import { supabase, API_BASE } from '../lib/supabase'
import { mapApiCourse, mapApiSearchResult, courseToSupabaseRow, mergeTeeOverrides } from '../utils/courseMapper'
import { geocodeCourse } from '../utils/geolocation'
import logger from '../utils/logger'

/**
 * Get the current session's access token for authenticated API calls.
 */
async function getAccessToken() {
  const { data: { session } } = await supabase.auth.getSession()
  return session?.access_token || null
}

/**
 * Search courses in Supabase using full-text + trigram matching.
 */
export async function searchCourses(query) {
  if (!query || query.length < 2) return []

  // Use trigram similarity for flexible matching
  const { data, error } = await supabase
    .from('courses')
    .select('id, course_name, short_name, club_name, city, state, country, holes, latitude, longitude')
    .or(`course_name.ilike.%${query}%,club_name.ilike.%${query}%,short_name.ilike.%${query}%,city.ilike.%${query}%`)
    .limit(20)

  if (error) {
    logger.error('Course search error: ' + error?.message)
    return []
  }

  return data || []
}

/**
 * Search courses via the Vercel API proxy (fallback for courses not in DB).
 */
export async function searchCoursesOnline(query) {
  if (!query || query.length < 2) return []

  const token = await getAccessToken()
  const response = await fetch(`${API_BASE}/api/courses/search?q=${encodeURIComponent(query)}`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
  })
  if (!response.ok) {
    logger.error('Online course search failed: ' + response.status)
    return []
  }

  const data = await response.json()
  const courses = data.courses || data.results || []
  return courses.map(mapApiSearchResult).filter(Boolean)
}

/**
 * Fetch full course details from Supabase.
 */
export async function getCourseFromDb(courseId) {
  if (!courseId) return null

  const { data, error } = await supabase
    .from('courses')
    .select('*')
    .eq('id', courseId)
    .single()

  if (error || !data) return null

  // Parse tees from JSONB + add camelCase aliases for app scoring/display code
  const tees = typeof data.tees === 'string' ? JSON.parse(data.tees) : data.tees || []
  const course = {
    ...data,
    tees,
    name: data.course_name,
    shortName: data.short_name || data.course_name,
    handicapIndex: data.handicap_index,
  }

  // Backfill coordinates if missing (fire-and-forget)
  if (!course.latitude && (course.city || course.state)) {
    geocodeCourse(course.course_name, course.city, course.state)
      .then((coords) => {
        if (coords) {
          supabase.rpc('backfill_course_coords', {
            course_id: course.id,
            lat: coords.latitude,
            lng: coords.longitude,
          }).then(({ error: err }) => {
            if (err) logger.warn('Coord backfill failed: ' + err.message)
          })
        }
      })
      .catch(() => {})
  }

  return course
}

/**
 * Fetch full course details from the API proxy (for courses not yet in DB).
 * The Vercel proxy caches the result in Supabase via service_role.
 */
export async function fetchCourseOnline(courseId) {
  if (!courseId) return null

  const token = await getAccessToken()
  const response = await fetch(`${API_BASE}/api/courses/${encodeURIComponent(courseId)}`, {
    headers: token ? { 'Authorization': `Bearer ${token}` } : {},
  })
  if (!response.ok) return null

  const data = await response.json()
  const course = data.course || data
  const mapped = mapApiCourse(course)

  // Geocode if the API didn't return coordinates (it usually doesn't)
  if (mapped && !mapped.latitude && (mapped.city || mapped.state)) {
    const coords = await geocodeCourse(mapped.course_name, mapped.city, mapped.state).catch(() => null)
    if (coords) {
      mapped.latitude = coords.latitude
      mapped.longitude = coords.longitude
    }
  }

  // Cache in Supabase so the course appears in local search next time.
  // In local dev the Vite proxy bypasses the Vercel serverless function,
  // so the server-side cache never runs — this client-side upsert ensures
  // courses are stored regardless of environment.
  // Merge existing per-tee overrides (e.g. manually-corrected handicapIndex)
  // so the API re-fetch doesn't wipe them.
  if (mapped) {
    supabase
      .from('courses')
      .select('source, tees')
      .eq('id', mapped.id)
      .maybeSingle()
      .then(({ data: existing }) => {
        // Never overwrite manually-corrected courses (e.g. Bandon Dunes)
        if (existing?.source === 'manual') return
        if (existing?.tees) {
          const existingTees = typeof existing.tees === 'string' ? JSON.parse(existing.tees) : existing.tees
          mapped.tees = mergeTeeOverrides(mapped.tees, existingTees)
        }
        return supabase.from('courses').upsert(courseToSupabaseRow(mapped), { onConflict: 'id' })
      })
      .then((res) => {
        if (res?.error) logger.warn('Course cache failed: ' + res.error.message)
      })
      .catch(() => {})
  }

  return mapped
}

/**
 * Fetch nearby courses using the nearby_courses RPC (earthdistance).
 */
export async function getNearbyCourses(lat, lng, limit = 10) {
  const { data, error } = await supabase.rpc('nearby_courses', {
    user_lat: lat,
    user_lng: lng,
    result_limit: limit,
  })
  if (error) {
    logger.error('Nearby courses error: ' + error?.message)
    return []
  }
  return data || []
}
